import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export const CONSENT_KEY = "tb-cookie-consent";

export const getConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
  } catch {
    return null;
  }
};

const purgeTracking = () => {
  Object.keys(localStorage)
    .filter((k) => k.startsWith("ph_"))
    .forEach((k) => localStorage.removeItem(k));
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (name.startsWith("ph_")) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  });
};

export const saveConsent = (analytics) => {
  const wasAccepted = getConsent()?.analytics;
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ analytics, ts: Date.now() }));
  if (analytics) {
    window.__initAnalytics?.();
  } else {
    if (window.posthog?.opt_out_capturing) window.posthog.opt_out_capturing();
    purgeTracking();
    if (wasAccepted && window.posthog?.capture) window.location.reload();
  }
};

export const openCookieSettings = () => window.dispatchEvent(new Event("tb:open-cookie-settings"));

const CookieConsent = () => {
  const [visible, setVisible] = useState(() => getConsent() === null);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const open = () => {
      setAnalytics(getConsent()?.analytics ?? false);
      setCustomize(true);
      setVisible(true);
    };
    window.addEventListener("tb:open-cookie-settings", open);
    return () => window.removeEventListener("tb:open-cookie-settings", open);
  }, []);

  if (!visible) return null;

  const choose = (value) => {
    saveConsent(value);
    setVisible(false);
  };

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-label="Cookie-samtykke"
      className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6"
    >
      <div className="mx-auto max-w-2xl rounded-lg border border-[#D6C2A6]/60 bg-[#FAF7F1] p-6 shadow-[0_18px_50px_rgba(47,51,56,0.14)] sm:p-8">
        <h2 className="font-serif text-xl text-[#3F5A47] sm:text-2xl">Dit samtykke</h2>
        <p className="mt-3 text-sm leading-relaxed text-[#2F3338]/80">
          Vi bruger kun teknisk nødvendig lagring for at huske dit valg. Hvis du accepterer, bruger
          vi desuden statistikværktøjet PostHog til at forstå, hvordan hjemmesiden bruges, så vi kan
          forbedre den. Du kan altid ændre eller trække dit samtykke tilbage. Læs mere i vores{" "}
          <Link
            to="/privatlivspolitik"
            data-testid="cookie-consent-policy-link"
            className="underline underline-offset-2 transition-colors hover:text-[#B46A45]"
          >
            privatlivs- og cookiepolitik
          </Link>
          .
        </p>

        {customize && (
          <div className="mt-5 space-y-4 border-t border-[#D6C2A6]/50 pt-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-[#2F3338]">Nødvendige</p>
                <p className="mt-0.5 text-xs text-[#2F3338]/60">
                  Husker dit samtykkevalg. Kan ikke fravælges.
                </p>
              </div>
              <span className="text-xs font-medium uppercase tracking-widest text-[#3F5A47]">
                Altid aktiv
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-[#2F3338]">Statistik</p>
                <p className="mt-0.5 text-xs text-[#2F3338]/60">
                  PostHog: anonyme besøgsstatistikker og sessionsoptagelse via vores
                  hostingplatform. Aktiveres kun med dit samtykke.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analytics}
                data-testid="cookie-toggle-analytics"
                onClick={() => setAnalytics((v) => !v)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${
                  analytics ? "bg-[#3F5A47]" : "bg-[#D6C2A6]/60"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-[#FAF7F1] transition-all duration-300 ${
                    analytics ? "left-[22px]" : "left-0.5"
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="cookie-accept-button"
            onClick={() => choose(true)}
            className="rounded-full bg-[#3F5A47] px-6 py-2.5 text-sm font-medium text-[#FAF7F1] transition-colors duration-300 hover:bg-[#2F3338]"
          >
            Accepter
          </button>
          <button
            type="button"
            data-testid="cookie-reject-button"
            onClick={() => choose(false)}
            className="rounded-full border border-[#3F5A47]/40 px-6 py-2.5 text-sm font-medium text-[#3F5A47] transition-colors duration-300 hover:border-[#B46A45] hover:text-[#B46A45]"
          >
            Afvis
          </button>
          {customize ? (
            <button
              type="button"
              data-testid="cookie-save-button"
              onClick={() => choose(analytics)}
              className="text-sm text-[#2F3338]/70 underline underline-offset-4 transition-colors hover:text-[#B46A45]"
            >
              Gem valg
            </button>
          ) : (
            <button
              type="button"
              data-testid="cookie-customize-button"
              onClick={() => setCustomize(true)}
              className="text-sm text-[#2F3338]/70 underline underline-offset-4 transition-colors hover:text-[#B46A45]"
            >
              Tilpas
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
